import type { AuthFailure } from './types'

/**
 * What a failed sign-in says to the person in front of the screen.
 *
 * Three things every message has to do, because the reader is usually alone at
 * a counter with a queue:
 *
 *   Say what happened, in their words rather than the system's.
 *   Say what to do next, try again, ask the owner, check the wifi.
 *   Carry a short code, so "it says ERR03" is enough for whoever they ring.
 *
 * The codes are stable and never reused. They are the only part of these
 * strings that survives being read out over a phone, which is why they are
 * ASCII: the old GİRİŞ-n form put two dotted capitals into something people
 * spell out down a bad line, and neither survives being repeated back.
 *
 * The map keys keep their own names (GIRIS-n). They are internal and every
 * call site already uses them; renaming both at once would be a rename of two
 * things for the benefit of neither.
 */

export interface FriendlyError {
  /** Shown large. One sentence, no jargon. */
  title: string
  /** Shown under it: what to do about it. */
  hint?: string
  /** Printed small, for reporting. */
  code: string
}

export const AUTH_ERRORS: Record<string, FriendlyError> = {
  /** The password did not match, or there is no such username. */
  'GIRIS-1': {
    title: 'Kullanıcı adı veya PIN hatalı.',
    hint: 'Kullanıcı adınızı ve PIN’inizi kontrol edip tekrar deneyin.',
    code: 'ERR01',
  },

  /**
   * Signed in, but no longer on the staff list.
   *
   * Named rather than described. "Yetkiniz yok" leaves someone wondering
   * whether they mistyped the PIN, and they will try it four more times.
   */
  'GIRIS-2': {
    title: 'Bu kullanıcı sistemde tanımlı değil.',
    hint: 'Hesabınız kaldırılmış olabilir. Kurum yöneticinizden yeniden tanımlamasını isteyin.',
    code: 'ERR02',
  },

  /** No route to the server. */
  'GIRIS-3': {
    title: 'İnternet bağlantısı yok.',
    hint: 'Giriş yapmak için bağlantı gerekiyor. Wi-Fi’yi kontrol edip tekrar deneyin.',
    code: 'ERR03',
  },

  /** Too many attempts in a short time. */
  'GIRIS-4': {
    title: 'Çok fazla deneme yapıldı.',
    hint: 'Güvenlik için bir süre beklemeniz gerekiyor. Birkaç dakika sonra tekrar deneyin.',
    code: 'ERR04',
  },

  /**
   * The address in the browser does not name an institution.
   *
   * Almost always a wrong or truncated link.
   */
  'GIRIS-5': {
    title: 'Kurum belirlenemedi.',
    hint: 'Adresin doğru olduğundan emin olun. Bağlantıyı kurum yöneticinizden yeniden isteyin.',
    code: 'ERR05',
  },

  /** The app was built without database credentials. */
  'GIRIS-6': {
    title: 'Uygulama ayarları eksik.',
    hint: 'Bu bir kurulum hatası. Yöneticinize ERR06 kodunu bildirin.',
    code: 'ERR06',
  },

  /** Signed in, but the account has no institution at all. */
  'GIRIS-7': {
    title: 'Hesabınız bir kuruma bağlı değil.',
    hint: 'Yöneticinizden hesabınızı bir kuruma tanımlamasını isteyin.',
    code: 'ERR07',
  },

  /**
   * The password is right but the address was never confirmed.
   *
   * Said plainly: reported as a wrong password, it sends someone to reset a
   * password that was never the problem, and the reset mail goes to the
   * same unconfirmed address.
   */
  'GIRIS-8': {
    title: 'E-posta adresiniz henüz onaylanmadı.',
    hint: 'Gelen kutunuzdaki postada “Onayla ve giriş yap” düğmesine basın. Gelmediyse gereksiz (spam) klasörüne bakın.',
    code: 'ERR08',
  },

  /**
   * The institution asked for its account to be deleted (Hesabım > Hesabı
   * sil, 23 Eki 2026): every login is banned until it is restored or gone.
   */
  'GIRIS-9': {
    title: 'Bu işletmenin hesabı kapatıldı.',
    hint: 'Hesap silme talebi alındı. Vazgeçtiyseniz 30 gün içinde info@adissoo.com adresine yazın.',
    code: 'ERR09',
  },

  /** Anything unrecognised. Deliberately last. */
  'GIRIS-0': {
    title: 'Giriş yapılamadı.',
    hint: 'Tekrar deneyin. Sorun sürerse yöneticinize ERR00 kodunu bildirin.',
    code: 'ERR00',
  },
}

/** Maps the failure kinds the auth layer reports onto the messages above. */
export function errorFor(failure: AuthFailure): FriendlyError {
  switch (failure) {
    case 'bad-credentials':
      return AUTH_ERRORS['GIRIS-1']
    case 'not-confirmed':
      return AUTH_ERRORS['GIRIS-8']
    case 'closed':
      return AUTH_ERRORS['GIRIS-9']
    case 'not-a-member':
      return AUTH_ERRORS['GIRIS-2']
    case 'offline':
      return AUTH_ERRORS['GIRIS-3']
    default:
      return AUTH_ERRORS['GIRIS-0']
  }
}
